/**
 * Created by piotr.pozniak@thebeaverhead.com on 11/04/2023.
 */

import React, { useCallback, useContext, useMemo } from "react";
import Separated from "./Separated";
import Conjunction from "./Conjunction";
import AddButton from "./AddButton";
import Rule from "./Rule";
import { ConjunctionContext, FiltersContext } from "./contexts";

/**
 * @typedef {Object} Field
 * @property {string} value - The internal value of the field.
 * @property {string} label - The display label of the field.
 */

/**
 * @typedef {Object} Operator
 * @property {string} value - The internal value of the operator.
 * @property {string} label - The display label of the operator.
 */

/**
 * @typedef {Object} Rule
 * @property {Field} field - The field used in the rule.
 * @property {Operator} operator - The operator used in the rule.
 * @property {string} value - The value to compare against.
 * @property {string} label - The display label of the rule.
 */

/**
 * @param {Rule[]} rules - The list of rules within this group.
 * @param {Object} conjunction
 * @param {string} conjunction.value
 * @param {Array} conjunction.labels
 * @param {function} conjunction.onChange
 * @param {function} conjunction.toggleHovered
 * @param {Number} groupId
 * @returns {React.JSX.Element}
 * @constructor
 */
const FilterGroup = ({ rules, conjunction, groupId }) => {
  const [hovered, setHovered] = React.useState(false);
  const { updateGroup, filters: _originalFilters } = useContext(FiltersContext);

  /**
   *
   * @type {(function(*): void)|*}
   */
  const onRemoveRule = useCallback(
    (id) => {
      // remove rules array item under id index

      rules.splice(id, 1);

      updateGroup(groupId, { conjunction, rules: [...rules] });
    },
    [conjunction, groupId, rules, updateGroup]
  );

  /**
   *
   * @type {(function(): void)|*}
   */
  const onConjunctionHover = useCallback(() => {
    setHovered(!hovered);
  }, [hovered]);

  /**
   *
   * @type {(function(*): void)|*}
   */
  const onConjunctionChange = useCallback(
    (val) => {
      setHovered(false);
      updateGroup(groupId, {
        ..._originalFilters.groups[groupId],
        conjunction: val,
      });
    },
    [updateGroup, groupId, _originalFilters.groups]
  );

  const filters = useMemo(() => {
    return rules.map((rule, index) => {
      return (
        <Rule
          key={`${groupId}-r-${index}`}
          groupId={groupId}
          id={index}
          rule={rule}
          onRemoveRule={onRemoveRule}
        />
      );
    });
  }, [groupId, onRemoveRule, rules]);

  return (
    <ConjunctionContext.Provider value={{ hovered }}>
      <div className={"filters__filter-group-container"}>
        <div className={"filters__filter-group"}>
          <Separated
            id={groupId}
            separator={
              <Conjunction
                value={conjunction}
                onChange={onConjunctionChange}
                toggleHovered={onConjunctionHover}
              />
            }
          >
            {filters}
          </Separated>
        </div>
        <AddButton groupId={groupId}>
          <div className={"filter-group__add-button"}>+</div>
        </AddButton>
      </div>
    </ConjunctionContext.Provider>
  );
};

export default FilterGroup;
